import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileJson, Languages, Upload } from "lucide-react";
import { api, ApiError, type Survey } from "../api/client";

const languages = ["en", "sr-Latn", "ru", "de", "fr", "es", "pt", "he"] as const;

export function TranslationsPage() {
  const queryClient = useQueryClient();
  const [uiLanguage, setUiLanguage] = useState<(typeof languages)[number]>("en");
  const [surveyId, setSurveyId] = useState("");
  const [surveyLanguage, setSurveyLanguage] = useState<(typeof languages)[number]>("en");
  const [uiResult, setUiResult] = useState<Record<string, unknown> | null>(null);
  const [surveyResult, setSurveyResult] = useState<Record<string, unknown> | null>(null);
  const [pendingUiImport, setPendingUiImport] = useState<{ language: string; fileName: string; dictionary: Record<string, unknown>; apply: boolean } | null>(null);
  const surveys = useQuery({ queryKey: ["surveys"], queryFn: () => api<Survey[]>("/api/admin/surveys") });

  const importUi = useMutation({
    mutationFn: (body: { language: string; fileName: string; dictionary: Record<string, unknown>; apply: boolean }) => api<Record<string, unknown>>(`/api/admin/translations/ui/import/${body.language}`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: (result, body) => {
      setUiResult(result);
      setPendingUiImport(!body.apply ? body : null);
      if (body.apply) void queryClient.invalidateQueries();
    },
    onError: (error) => { setPendingUiImport(null); setUiResult(errorResult(error)); }
  });

  const importSurvey = useMutation({
    mutationFn: (body: unknown) => api<Record<string, unknown>>(`/api/admin/translations/surveys/${surveyId}/import`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: setSurveyResult,
    onError: (error) => setSurveyResult(errorResult(error))
  });

  function submitUiImport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try { importUi.mutate({
      language: uiLanguage,
      fileName: `ui-${uiLanguage}.json`,
      dictionary: parseJson(String(form.get("dictionary") || "{}")),
      apply: false
    }); } catch (error) { setPendingUiImport(null); setUiResult(errorResult(error)); }
  }

  function submitSurveyImport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!surveyId) return;
    const form = new FormData(event.currentTarget);
    try {
      const payload = parseJson(String(form.get("payload") || "{}"));
      importSurvey.mutate({ ...payload, language: surveyLanguage, fileName: `survey-${surveyId}-${surveyLanguage}.json` });
    } catch (error) { setSurveyResult(errorResult(error)); }
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card translations-hero">
        <div>
          <span className="panel-eyebrow left">Translations</span>
          <h2>UI i survey prevodi bez gubitka stable key vrijednosti.</h2>
          <p>Survey import validira `internal_key` i option value polja; guest anketa i dalje dobija samo potreban jezik.</p>
        </div>
        <div className="object-brand-card">
          <Languages size={34} />
          <strong>8 jezika</strong>
          <span>EN / ME / RU / DE / FR / ES / PT / HE</span>
        </div>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>UI prevodi</h2>
              <p>Provjeri JSON i promjene prije primjene. Sačuvani prevodi važe za tvoju organizaciju i ostaju dostupni nakon restarta.</p>
            </div>
            <a className="button-link" href={`/api/admin/translations/ui/export/${uiLanguage}`}><Download size={16} /> Export</a>
          </div>
          <form className="compact-form" onSubmit={submitUiImport}>
            <select value={uiLanguage} onChange={(event) => { setUiLanguage(event.target.value as (typeof languages)[number]); setPendingUiImport(null); setUiResult(null); }} disabled={importUi.isPending}>
              {languages.map((language) => <option key={language} value={language}>{language}</option>)}
            </select>
            <textarea name="dictionary" rows={12} placeholder='{"guest":{"actions":{"next":"Next"}}}' required onChange={() => setPendingUiImport(null)} />
            <button type="submit" disabled={importUi.isPending}><Upload size={16} /> Provjeri JSON</button>
          </form>
          {uiResult && <pre className="result-box">{JSON.stringify(uiResult, null, 2)}</pre>}
          {pendingUiImport && <button type="button" disabled={importUi.isPending} onClick={() => importUi.mutate({ ...pendingUiImport, apply: true })}>Primijeni prevode</button>}
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Survey prevodi</h2>
              <p>Export sadrzi survey tekstove, pitanja i stabilne option value-e za prevodioce.</p>
            </div>
            {surveyId && (
              <a className="button-link" href={`/api/admin/translations/surveys/${surveyId}/export?language=${surveyLanguage}`}>
                <Download size={16} /> Export
              </a>
            )}
          </div>
          <form className="compact-form" onSubmit={submitSurveyImport}>
            <select value={surveyId} onChange={(event) => setSurveyId(event.target.value)} required>
              <option value="">Izaberi anketu</option>
              {(surveys.data ?? []).map((survey) => <option key={survey.id} value={survey.id}>{survey.name}</option>)}
            </select>
            <select value={surveyLanguage} onChange={(event) => setSurveyLanguage(event.target.value as (typeof languages)[number])}>
              {languages.map((language) => <option key={language} value={language}>{language}</option>)}
            </select>
            <textarea name="payload" rows={12} placeholder='{"survey":{"title":"..."}, "questions":[{"internalKey":"room_cleanliness","label":"...","options":null}]}' required />
            <button type="submit"><FileJson size={16} /> Import survey prevoda</button>
          </form>
          {surveyResult && <pre className="result-box">{JSON.stringify(surveyResult, null, 2)}</pre>}
        </section>
      </div>
    </div>
  );
}

function parseJson(value: string) {
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("JSON mora sadržati objekat.");
  return parsed as Record<string, unknown>;
}

function errorResult(error: unknown): Record<string, unknown> {
  if (error instanceof ApiError) {
    try {
      return { status: error.status, ...JSON.parse(error.message) };
    } catch {
      return { status: error.status, error: error.message };
    }
  }
  return { error: error instanceof Error ? error.message : "Unknown error" };
}
